import { CreateLocalTrackOptions as TwilioCreateLocalTrackOptions } from "twilio-video";
import { logger } from "../../../datadog/logger";
import { AudioVideoMediaKind } from "../../core/room/participant/media/types";
import {
  AudioVideoSettings,
  LocalAVTrack,
  LocalAVTrackContextAtom,
  LocalMediaStatus,
} from "../../core/types";
import {
  constraintsToValue,
  getStoredAudioVideoSettings,
} from "../../core/utils";
import {} from "../utils";
import { initNoiseCancellation } from "./audio/noiseCancellationUtils";
import { TrackOperationResult } from "./types";
import { createLocalAudioTrack, createLocalVideoTrack } from "./utils";

/**
 * Creates a local media track (audio or video), sets local state, handles errors gracefully
 */
export async function gracefullyCreateLocalTrack(
  trackKind: AudioVideoMediaKind,
  trackOptions: TwilioCreateLocalTrackOptions,
  previousLocalTrack: LocalAVTrackContextAtom,
  setStoredAudioVideoSettings: (
    updater: (prev: AudioVideoSettings) => AudioVideoSettings,
  ) => void,
): Promise<TrackOperationResult> {
  const targetDeviceId = trackOptions?.deviceId;
  const storedAudioVideoSettings = getStoredAudioVideoSettings();
  // Extract actual device ID value from constraints if provided
  const extractedDeviceId = targetDeviceId
    ? constraintsToValue<string>(targetDeviceId)
    : undefined;

  let track: LocalAVTrack;

  try {
    switch (trackKind) {
      case "audio": {
        const deviceId =
          targetDeviceId ?? storedAudioVideoSettings?.inputAudioDeviceId;

        track = await createLocalAudioTrack({
          ...trackOptions,
          deviceId,
        });

        if (!track) {
          return {
            success: false,
            status: previousLocalTrack?.status ?? LocalMediaStatus.ERROR,
            track: previousLocalTrack?.track,
          };
        }

        // Store the resolved device ID for future use
        setStoredAudioVideoSettings((prevSettings) => ({
          ...prevSettings,
          inputAudioDeviceId: extractedDeviceId,
        }));

        await initNoiseCancellation({
          localAudioTrack: track,
          setStoredAudioVideoSettings,
        });
        break;
      }
      case "video": {
        const deviceId =
          targetDeviceId ?? storedAudioVideoSettings?.inputVideoDeviceId;
        track = await createLocalVideoTrack({
          ...trackOptions,
          deviceId,
        });
        if (!track) {
          return {
            success: false,
            status: previousLocalTrack?.status ?? LocalMediaStatus.ERROR,
            track: previousLocalTrack?.track,
          };
        }

        // Store the resolved device ID for future use
        setStoredAudioVideoSettings((prevSettings) => ({
          ...prevSettings,
          inputVideoDeviceId: extractedDeviceId,
        }));
        break;
      }
      default:
        return { success: false, ...previousLocalTrack };
    }

    // Common logic for setting final state
    const shouldEnableTrack =
      !previousLocalTrack?.status ||
      previousLocalTrack.status === LocalMediaStatus.READY;

    return {
      success: true,
      status: shouldEnableTrack
        ? LocalMediaStatus.READY
        : LocalMediaStatus.DISABLED,
      track,
    };
  } catch (error) {
    logger.error(
      `Unexpected error when creating local ${trackKind} track`,
      { trackOptions },
      error as Error,
    );

    return { success: false, status: LocalMediaStatus.ERROR };
  }
}
